/**
 * Un lector del subconjunto de Markdown que usan los documentos legales.
 *
 * No es un parser de Markdown y no pretende serlo. Lee exactamente lo que los
 * términos de uso y la política de privacidad usan: títulos, párrafos, listas,
 * tablas, reglas, negritas, código y enlaces.
 *
 * Existe en vez de una dependencia por dos razones. La primera es el costo: el
 * frontend tiene nueve dependencias y cada una entra en el análisis de
 * vulnerabilidades del pipeline, lo cual es mucho peso para dos documentos
 * estáticos. La segunda es que devuelve una estructura, no HTML, de modo que la
 * página la pinta con elementos de React y no hace falta inyectar marcado en
 * una pantalla donde el usuario acepta obligaciones.
 *
 * Si algún día los documentos usan algo que esto no entiende, lo muestra como
 * párrafo en vez de perderlo.
 */

/** Parte una línea en fragmentos con formato: negrita, código y enlaces. */
export function leerLinea(texto) {
    const piezas = [];
    // Un solo recorrido con las tres formas alternativas, para que el orden en
    // que aparecen no cambie el resultado.
    const patron = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)/g;
    let ultimo = 0;
    let coincidencia;

    while ((coincidencia = patron.exec(texto)) !== null) {
        if (coincidencia.index > ultimo) {
            piezas.push({ tipo: 'texto', texto: texto.slice(ultimo, coincidencia.index) });
        }
        if (coincidencia[1] !== undefined) {
            piezas.push({ tipo: 'fuerte', texto: coincidencia[1] });
        } else if (coincidencia[2] !== undefined) {
            piezas.push({ tipo: 'codigo', texto: coincidencia[2] });
        } else {
            piezas.push({ tipo: 'enlace', texto: coincidencia[3], destino: coincidencia[4] });
        }
        ultimo = patron.lastIndex;
    }
    if (ultimo < texto.length) {
        piezas.push({ tipo: 'texto', texto: texto.slice(ultimo) });
    }
    return piezas;
}

const celdas = (linea) => linea.split('|').slice(1, -1).map(c => c.trim());

/** Una línea de separación de tabla: `| --- | --- |` o `|---|---|`. */
const esSeparadorDeTabla = (linea) => /^\|[\s:|-]+\|$/.test(linea) && linea.includes('-');

/**
 * Convierte el documento en una lista de bloques.
 *
 * @returns {Array<{tipo: string, ...}>}
 */
export function leerMarkdown(fuente) {
    const lineas = String(fuente ?? '').replace(/\r\n/g, '\n').split('\n');
    const bloques = [];
    let i = 0;

    const vaciarParrafo = (acumulado) => {
        if (acumulado.length) {
            bloques.push({ tipo: 'parrafo', piezas: leerLinea(acumulado.join(' ')) });
        }
        return [];
    };

    let parrafo = [];

    while (i < lineas.length) {
        const linea = lineas[i];
        const limpia = linea.trim();

        if (!limpia) {
            parrafo = vaciarParrafo(parrafo);
            i += 1;
            continue;
        }

        // Regla horizontal. Se comprueba antes del título porque `---` también
        // empieza con un guion.
        if (/^-{3,}$/.test(limpia)) {
            parrafo = vaciarParrafo(parrafo);
            bloques.push({ tipo: 'regla' });
            i += 1;
            continue;
        }

        const titulo = limpia.match(/^(#{1,4})\s+(.*)$/);
        if (titulo) {
            parrafo = vaciarParrafo(parrafo);
            bloques.push({
                tipo: 'titulo',
                nivel: titulo[1].length,
                piezas: leerLinea(titulo[2]),
            });
            i += 1;
            continue;
        }

        // Tabla: una fila de encabezado seguida de la línea de separación.
        if (limpia.startsWith('|') && esSeparadorDeTabla((lineas[i + 1] || '').trim())) {
            parrafo = vaciarParrafo(parrafo);
            const encabezado = celdas(limpia).map(leerLinea);
            const filas = [];
            i += 2;
            while (i < lineas.length && lineas[i].trim().startsWith('|')) {
                filas.push(celdas(lineas[i].trim()).map(leerLinea));
                i += 1;
            }
            bloques.push({ tipo: 'tabla', encabezado, filas });
            continue;
        }

        // Lista. Las continuaciones de un mismo punto vienen sangradas, así que
        // se pegan al punto anterior en vez de abrir uno nuevo.
        if (/^[-*]\s+/.test(limpia)) {
            parrafo = vaciarParrafo(parrafo);
            const puntos = [];
            while (i < lineas.length) {
                const actual = lineas[i];
                const suLimpia = actual.trim();
                if (/^[-*]\s+/.test(suLimpia)) {
                    puntos.push(suLimpia.replace(/^[-*]\s+/, ''));
                    i += 1;
                } else if (suLimpia && /^\s{2,}/.test(actual) && puntos.length) {
                    puntos[puntos.length - 1] += ` ${suLimpia}`;
                    i += 1;
                } else {
                    break;
                }
            }
            bloques.push({ tipo: 'lista', puntos: puntos.map(leerLinea) });
            continue;
        }

        parrafo.push(limpia);
        i += 1;
    }

    vaciarParrafo(parrafo);
    return bloques;
}
